/**
 * TrendIndicator Component
 *
 * Displays a trend direction (improving/stable/declining) with an arrow
 */

import React from 'react';
import {
  TrendingDown as DecliningIcon,
  TrendingFlat as StableIcon,
  TrendingUp as ImprovingIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { TREND_COLORS } from '../../constants';
import type { TrendDirection } from '../../types';

interface TrendIndicatorProps {
  /** The trend direction */
  trend: TrendDirection | string | null | undefined;
  /** Whether to show the label */
  showLabel?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

const TREND_ICONS: Record<TrendDirection, React.ReactNode> = {
  improving: <ImprovingIcon fontSize="inherit" />,
  stable: <StableIcon fontSize="inherit" />,
  declining: <DecliningIcon fontSize="inherit" />,
};

const TREND_LABELS: Record<TrendDirection, string> = {
  improving: 'Improving',
  stable: 'Stable',
  declining: 'Declining',
};

export const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  trend,
  showLabel = false,
  size = 'small',
}) => {
  if (!trend || !['improving', 'stable', 'declining'].includes(trend)) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.disabled',
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        }}
      >
        —
      </Typography>
    );
  }

  const validTrend = trend as TrendDirection;
  const color = TREND_COLORS[validTrend];
  const iconSize = size === 'small' ? '1rem' : '1.25rem';

  if (!showLabel) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          color,
          fontSize: iconSize,
        }}
        title={TREND_LABELS[validTrend]}
      >
        {TREND_ICONS[validTrend]}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        color,
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        fontWeight: 500,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', fontSize: iconSize }}>
        {TREND_ICONS[validTrend]}
      </Box>
      <Typography variant="inherit" component="span">
        {TREND_LABELS[validTrend]}
      </Typography>
    </Box>
  );
};

export default TrendIndicator;

